import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Sidebar from '../../components/ui/Sidebar';
import QuickActionsToolbar from '../../components/ui/QuickActionsToolbar';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import projectService from '../../services/projectService';
import projectDashboardService from '../../services/projectDashboardService';
import ProjectProgressOverview from './components/ProjectProgressOverview';
import ModuleList from './components/ModuleList';
import QAQCSummary from './components/QAQCSummary';
import HSESummary from './components/HSESummary';
import ResourceAllocation from './components/ResourceAllocation';
import BudgetSummary from './components/BudgetSummary';
import CashFlowSummary from './components/CashFlowSummary';
import RiskPredictions from './components/RiskPredictions';
import CreateModuleModal from './components/CreateModuleModal';
import ProjectFileManager from './components/ProjectFileManager';
import Breadcrumb from '../module-landing-page/components/Breadcrumb';

const ProjectSpecificDashboard = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [project, setProject] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModuleModal, setShowCreateModuleModal] = useState(false);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    if (projectId) {
      fetchProjectData();
    }
  }, [projectId]);

  const fetchProjectData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch project details
      const { data: projectData, error: projectError } = await projectService?.getProjectById(projectId);
      if (projectError) throw new Error(projectError);

      setProject({
        id: projectData?.id,
        name: projectData?.name,
        description: projectData?.description,
        status: projectData?.status,
        progress: projectData?.progress,
        owner: projectData?.owner,
        teamMembers: projectData?.team_members || [],
        imageUrl: projectData?.image_url
      });

      // Fetch all dashboard data
      const allData = await projectDashboardService?.getAllDashboardData(projectId);
      setDashboardData(allData);
    } catch (err) {
      setError(err?.message || 'Failed to load project data');
      console.error('Error loading project:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateModule = () => {
    setShowCreateModuleModal(true);
  };

  const handleModuleSubmit = async (moduleData) => {
    try {
      const { error } = await projectDashboardService?.createModule(moduleData);
      if (error) throw new Error(error);
      
      // Refresh dashboard data
      await fetchProjectData();
      setShowCreateModuleModal(false);
    } catch (err) {
      console.error('Error creating module:', err);
      alert(`Failed to create module: ${err?.message}`);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'var(--color-success)';
      case 'planning':
        return 'var(--color-warning)';
      case 'completed':
        return 'var(--color-success)';
      case 'on-hold':
        return 'var(--color-muted-foreground)';
      default:
        return 'var(--color-muted-foreground)';
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen bg-[var(--color-background)]">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-[var(--color-muted-foreground)]">
            <Icon name="loader" className="animate-spin" size={32} />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-screen bg-[var(--color-background)]">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <p className="text-[var(--color-destructive)] mb-4">{error}</p>
            <Button onClick={() => navigate('/projects')}>Back to Projects</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-[var(--color-background)]">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden transition-all duration-250" style={{ marginLeft: projectId ? 'calc(var(--sidebar-width, 240px) + var(--project-submenu-width, 192px))' : 'var(--sidebar-width, 240px)' }}>
        <QuickActionsToolbar />
        
        {/* Main Content */}
        <div className="flex-1 overflow-auto">
          <div className="p-6">
            {/* Breadcrumb */}
            <div className="mb-4">
              <Breadcrumb
                projectId={projectId}
                projectName={project?.name}
                moduleId={undefined}
                moduleName={undefined}
                workflowName={undefined}
              />
            </div>

            {/* Project Header with Logo */}
            <div className="flex items-center gap-4 mb-6">
              {/* Project Logo/Image */}
              <div className="w-16 h-16 rounded-lg overflow-hidden bg-[var(--color-muted)] flex items-center justify-center flex-shrink-0">
                {project?.imageUrl && !imageError ? (
                  <img
                    src={project?.imageUrl}
                    alt={project?.name || 'Project'}
                    className="w-full h-full object-cover"
                    onError={() => setImageError(true)}
                  />
                ) : (
                  <Icon name="folder" size={32} className="text-[var(--color-muted-foreground)]" />
                )}
              </div>

              {/* Project Info */}
              <div className="flex-1">
                <h1 className="text-2xl font-bold text-[var(--color-foreground)] mb-1">
                  {project?.name || 'Untitled Project'}
                </h1>
              </div>

              {/* Status Badge */}
              <div
                className="px-3 py-1 rounded-full text-sm font-medium"
                style={{
                  backgroundColor: `${getStatusColor(project?.status)}20`,
                  color: getStatusColor(project?.status)
                }}
              >
                {project?.status?.charAt(0)?.toUpperCase() + project?.status?.slice(1)}
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-3">
                <Button
                  size="sm"
                  iconName="Plus"
                  iconPosition="left"
                  onClick={handleCreateModule}
                  className="bg-blue-600 text-white hover:bg-orange-500 active:bg-orange-600 border-0"
                >
                  Create Module
                </Button>
              </div>
            </div>

            {/* Dashboard Content */}
            <div className="space-y-4">
              {/* Top Row: 4 equal 1/4 width columns */}
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                {/* 1/4: Project Progress Production Overview */}
                <div className="w-full">
                  <ProjectProgressOverview 
                    data={dashboardData?.progress} 
                    modules={dashboardData?.modules}
                    onProgressUpdate={fetchProjectData}
                  />
                </div>

                {/* 1/4: Resource Allocation */}
                <div className="w-full">
                  <ResourceAllocation 
                    data={dashboardData?.progress}
                    onResourceUpdate={fetchProjectData}
                  />
                </div>

                {/* 1/4: QA/QC Summary */}
                <div className="w-full">
                  <QAQCSummary 
                    data={dashboardData?.qaqc}
                    onQAQCUpdate={fetchProjectData}
                  />
                </div>

                {/* 1/4: HSE Summary */}
                <div className="w-full">
                  <HSESummary 
                    data={dashboardData?.hse}
                    onHSEUpdate={fetchProjectData}
                  />
                </div>
              </div>

              {/* Module List */}
              <div>
                <ModuleList modules={dashboardData?.modules} onModuleUpdated={fetchProjectData} />
              </div>

              {/* Project Files - Full Width */}
              <div>
                <ProjectFileManager 
                  projectId={projectId} 
                  isOwner={user?.email === project?.owner}
                />
              </div>

              {/* Bottom Row: Budget, Cash Flow, Risk Predictions */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Budget Summary */}
                <div>
                  <BudgetSummary 
                    data={dashboardData?.budget} 
                    modules={dashboardData?.modules}
                    onBudgetUpdate={fetchProjectData}
                  />
                </div>

                {/* Cash Flow Summary - spans 2 columns */}
                <div className="lg:col-span-2">
                  <CashFlowSummary 
                    cashflow={dashboardData?.cashflow}
                    onCashflowUpdate={fetchProjectData}
                  />
                </div>
              </div>

              {/* Risk Predictions - Full Width */}
              <div className="mb-32">
                <RiskPredictions 
                  risks={dashboardData?.risks}
                  onRiskUpdate={fetchProjectData}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Create Module Modal */}
      {showCreateModuleModal && (
        <CreateModuleModal
          onClose={() => setShowCreateModuleModal(false)}
          onSubmit={handleModuleSubmit}
          projectId={projectId}
        />
      )}
    </div>
  );
};

export default ProjectSpecificDashboard;